"use client";

import { ArrowUpRight, Star } from "lucide-react";
import { cn } from "@/lib/utils";
import { trackEvent } from "@/lib/analytics";
import {
  GOOGLE_REVIEWS_URL,
  RATING_ADVERTISED,
  type CustomerReview,
} from "@/lib/reviews";

export interface HeroTrustCopy {
  /** Names the number for screen readers — "4.6" alone says nothing. */
  ratingLabel: string;
  /** Fully composed, e.g. "800+ Google reviews". */
  reviewCountLabel: string;
  verifiedLabel: string;
  cta: string;
}

interface HeroTrustProps {
  /** `null` when the featured review has been removed; the card degrades. */
  review: CustomerReview | null;
  copy: HeroTrustCopy;
  locale: string;
}

const STARS = 5;

/**
 * A star row that actually represents the rating.
 *
 * Five outlined stars with a filled copy clipped to `rating / 5` of the width.
 * The obvious alternative — five solid stars beside the number "4.6" — is what
 * most sites ship, and it contradicts itself: the number says one thing and the
 * picture says perfect. Rounding to whole stars has the same problem one
 * decimal place later. A visitor who reads reviews for a living notices.
 */
function StarRating({ rating }: { rating: number }) {
  const filledWidth = `${(rating / STARS) * 100}%`;

  return (
    <span className="relative inline-flex" aria-hidden="true">
      <span className="flex gap-0.5">
        {Array.from({ length: STARS }, (_, i) => (
          <Star key={i} className="h-4 w-4 text-white/25" />
        ))}
      </span>
      <span
        className="absolute inset-y-0 left-0 flex gap-0.5 overflow-hidden"
        style={{ width: filledWidth }}
      >
        {Array.from({ length: STARS }, (_, i) => (
          <Star
            key={i}
            className="h-4 w-4 flex-none fill-yellow-500 text-yellow-500"
          />
        ))}
      </span>
    </span>
  );
}

/**
 * The hero's trust card — what occupies the offer slot when no promotion is
 * running.
 *
 * The slot is never empty and never changes shape. Offers and this card are
 * interchangeable tenants of the same grid area, so a visitor cannot tell
 * which state is the "real" hero; the alternative, collapsing to a centred
 * single column, makes the page look like it is reacting to missing data.
 *
 * Content order is deliberate: the aggregate first (the claim that is hard to
 * fake at 800+), then one named customer in their own words (the claim that is
 * hard to ignore). Reversing them leads with an anecdote a visitor has no
 * reason to weight yet.
 *
 * Styled as a card, not a button. The hero carries exactly one filled action
 * ("Book Service"); this is corroboration for that action, not competition
 * with it.
 */
export function HeroTrust({ review, copy, locale }: HeroTrustProps) {
  return (
    <a
      href={GOOGLE_REVIEWS_URL}
      target="_blank"
      // Without noopener the destination gets a handle on this window.
      rel="noopener noreferrer"
      onClick={() => trackEvent({ event: "hero_reviews_click", locale })}
      className={cn(
        "group relative flex h-full flex-col overflow-hidden rounded-[20px]",
        "border border-white/10 bg-site-card p-6 transition-colors duration-200 motion-reduce:transition-none",
        "hover:border-yellow-500/40 hover:bg-site-section",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-yellow-500",
      )}
    >
      {/* Accent edge — matches the offer cards this card stands in for */}
      <span
        aria-hidden="true"
        className="absolute inset-y-[14px] left-0 w-[3px] rounded-r-[3px] bg-yellow-500 opacity-60 transition-opacity group-hover:opacity-100 motion-reduce:transition-none"
      />

      <div className="flex items-center gap-3.5">
        {/* The label is real text, not an aria-label: a plain span has no
            role, and several readers drop aria-label on role-less elements
            when computing the enclosing link's name. */}
        <span className="text-[2.75rem] font-bold leading-none tracking-tight text-white tabular-nums">
          {RATING_ADVERTISED.toFixed(1)}
          <span className="sr-only"> {copy.ratingLabel}</span>
        </span>
        <span className="flex flex-col gap-1.5">
          <StarRating rating={RATING_ADVERTISED} />
          <span className="text-xs font-medium tracking-[0.04em] text-white/50">
            {copy.reviewCountLabel}
          </span>
        </span>
      </div>

      {review !== null && (
        <>
          {/* The quote's language is the customer's, not the page's — these
              reviews are real and untranslated, so the two routinely differ.
              Declaring it keeps a screen reader from reading Spanish aloud in
              an English voice. */}
          <p
            lang={review.lang}
            className="mt-5 text-[15px] leading-relaxed text-white/80 text-pretty"
          >
            {`“${review.content}”`}
          </p>
          <p className="mt-3 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-sm">
            <span className="font-semibold text-white">{review.author}</span>
            <span className="text-[10.5px] font-bold uppercase tracking-[0.13em] text-yellow-500/80">
              {copy.verifiedLabel}
            </span>
          </p>
        </>
      )}

      <span className="mt-auto inline-flex items-center gap-2 pt-5 text-sm font-semibold text-yellow-500">
        {copy.cta}
        <ArrowUpRight
          className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-[2px] group-hover:-translate-y-[2px] motion-reduce:transition-none motion-reduce:group-hover:transform-none"
          aria-hidden="true"
        />
      </span>
    </a>
  );
}
